import React from 'react'

import { theme } from '@acorns/grove'
import { Bold } from '@acorns/web-components'
import { useAnalyticsClient } from '@acorns/web-utils'
import styled from 'styled-components'

import {
  AiChatWidgetButtonTappedWidget_type,
  AiChatWidgetContainerViewedWidget_type,
  trackAiChatWidgetButtonTappedPayload,
  trackAiChatWidgetContainerViewedPayload,
} from 'generated/trackingEvents'
import {
  ChatChoice,
  ChatChoiceActionMetadata,
  ChatChoiceWidget,
} from 'generated/types'
import useAnalyticsComponentViewed from 'hooks/useAnalyticsComponentViewed'

const ReferralWidgetContainer = styled.div`
  margin: 20px 0;
`

const CampaignsTitle = styled(Bold)`
  color: ${theme.colors.primarySlate};
  margin-left: 40px;
`

const CampaignsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-left: 40px;
  margin-top: 10px;
`

const CampaignCard = styled.div<{ isDisabled: boolean }>`
  border-radius: 20px;
  padding: 15px 20px;
  box-shadow: ${theme.shadows.smallMedium};
  border: 1.5px solid ${theme.colors.naturalCloud};
  background: ${(props) =>
    props.isDisabled
      ? theme.colors.primaryIvoryLight
      : theme.colors.primaryWhite};
  cursor: ${(props) => (props.isDisabled ? 'default' : 'pointer')};
  ${(props) => props.isDisabled && 'opacity: 0.5;'};
`

const CampaignTitle = styled(Bold)`
  margin-bottom: 4px;
`

const CampaignStatus = styled.div`
  font-size: 14px;
  color: ${theme.colors.primaryStone};
  font-weight: 400;
  line-height: 22px;
`

interface ChoiceWidgetProps {
  chatChoices: ChatChoiceWidget
  onSendMessage: (
    searchTerm: string,
    metadata?: ChatChoiceActionMetadata,
  ) => void
  isDisabled: boolean
}

export const removeTypenameFromMutationPayload = (
  metadata: ChatChoiceActionMetadata,
) => {
  if (metadata.__typename) {
    delete metadata.__typename
  }
  if (metadata.actionData?.__typename) {
    delete metadata.actionData.__typename
  }
  return metadata
}

export const ChoiceWidget = ({
  chatChoices,
  onSendMessage,
  isDisabled,
}: ChoiceWidgetProps) => {
  const { track } = useAnalyticsClient()

  const handleCampaignClick = (choice: ChatChoice) => () => {
    if (isDisabled) {
      return
    }
    onSendMessage(
      choice.title,
      removeTypenameFromMutationPayload(choice.metadata),
    )
    track(
      ...trackAiChatWidgetButtonTappedPayload({
        widgetType: AiChatWidgetButtonTappedWidget_type.Choice_select,
        ctaTitle: chatChoices.title,
      }),
    )
  }

  const ref = useAnalyticsComponentViewed({
    generatedPayload: trackAiChatWidgetContainerViewedPayload({
      widgetType: AiChatWidgetContainerViewedWidget_type.Choice_select,
    }),
  })

  return (
    <ReferralWidgetContainer ref={ref}>
      <CampaignsTitle>{chatChoices.title}</CampaignsTitle>
      <CampaignsList>
        {chatChoices.choices.map((choice) => (
          <CampaignCard
            key={choice.id}
            onClick={handleCampaignClick(choice)}
            isDisabled={isDisabled}
          >
            <CampaignTitle>{choice.title}</CampaignTitle>
            <CampaignStatus>{choice.description}</CampaignStatus>
          </CampaignCard>
        ))}
      </CampaignsList>
    </ReferralWidgetContainer>
  )
}
